import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";

const Production = lazy(() => import("@/pages/Production"));
const WorkOrderDetail = lazy(() => import("@/pages/WorkOrderDetail"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const Operator = lazy(() => import("@/pages/Operator"));
const ProductionManagement = lazy(() => import("@/pages/ProductionManagement"));
const Assignments = lazy(() => import("@/pages/Assignments"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Productivity = lazy(() => import("@/pages/Productivity"));
const Products = lazy(() => import("@/pages/Products"));
const WarehousePage = lazy(() => import("@/pages/Warehouse"));
const RawMaterials = lazy(() => import("@/pages/RawMaterials"));
const Analysis = lazy(() => import("@/pages/Analysis"));
const Configurator = lazy(() => import("@/pages/Configurator"));
const Interventi = lazy(() => import("@/pages/Interventi"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Admin = lazy(() => import("@/pages/Admin"));
const Gantt = lazy(() => import("@/pages/Gantt"));
const Academy = lazy(() => import("@/pages/Academy"));
const ProcedureDetail = lazy(() => import("@/pages/ProcedureDetail"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const MachineStatus = lazy(() => import("@/pages/MachineStatus"));
const RawScan = lazy(() => import("@/pages/RawScan"));
const PlanningProduction = lazy(() => import("@/pages/PlanningProduction"));
const Quality = lazy(() => import("@/pages/Quality"));
const Purchasing = lazy(() => import("@/pages/Purchasing"));
const RichiesteInterne = lazy(() => import("@/pages/RichiesteInterne"));
const SupplyChecks = lazy(() => import("@/pages/SupplyChecks"));
function ProtectedBare({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  return children;
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role === "operator" ? "/operator" : "/dashboard"} replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedAssign({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  const ok = ["management", "reparto_tecnico", "amministrazione", "admin"].includes(user.role) || user.can_assign;
  if (!ok) return <Navigate to={user.role === "operator" ? "/operator" : "/dashboard"} replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedPlanning({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  const ok = ["management", "admin"].includes(user.role) || (user.permissions || []).includes("pianificazione");
  if (!ok) return <Navigate to={user.role === "operator" ? "/operator" : "/dashboard"} replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedQuality({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  const ok = ["management", "admin"].includes(user.role) || (user.permissions || []).includes("quality");
  if (!ok) return <Navigate to={user.role === "operator" ? "/operator" : "/dashboard"} replace />;
  return <Layout>{children}</Layout>;
}

function Root() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "operator" ? "/operator" : "/dashboard"} replace />;
}

function App() {
  // Prefetch dei chunk delle pagine più usate dopo il caricamento iniziale,
  // così la navigazione resta istantanea mantenendo il bundle iniziale leggero.
  useEffect(() => {
    const warm = () => {
      import("@/pages/Production"); import("@/pages/Warehouse"); import("@/pages/RawMaterials");
      import("@/pages/OrderDetail"); import("@/pages/Configurator"); import("@/pages/Calendar");
      import("@/pages/Interventi"); import("@/pages/Gantt"); import("@/pages/Products");
      import("@/pages/Operator"); import("@/pages/Assignments");
    };
    const ric = window.requestIdleCallback;
    const id = ric ? ric(warm, { timeout: 3000 }) : setTimeout(warm, 1500);
    return () => { if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, []);
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-right" theme="light" toastOptions={{ style: { background: "#ffffff", border: "1px solid #dde3ec", color: "#12233f", borderRadius: 4 } }} />
          <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-[#6f8199]">ABRAMS OS</div>}>
            <Routes>
              <Route path="/" element={<Root />} />
              <Route path="/login" element={<Login />} />
              <Route path="/m/:code" element={<ProtectedBare><MachineStatus /></ProtectedBare>} />
              <Route path="/mp/:id" element={<ProtectedBare><RawScan /></ProtectedBare>} />
              <Route path="/dashboard" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "sales", "warehouse", "admin"]}><Dashboard /></Protected>} />
              <Route path="/production" element={<Protected><Production /></Protected>} />
              <Route path="/production/order/:code" element={<Protected><OrderDetail /></Protected>} />
              <Route path="/production/:id" element={<Protected><WorkOrderDetail /></Protected>} />
              <Route path="/gestione-produzione" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "admin"]}><ProductionManagement /></Protected>} />
              <Route path="/operator" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "operator", "admin"]}><Operator /></Protected>} />
              <Route path="/assignments" element={<ProtectedAssign><Assignments /></ProtectedAssign>} />
              <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
              <Route path="/productivity" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "admin"]}><Productivity /></Protected>} />
              <Route path="/products" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "warehouse", "admin"]}><Products /></Protected>} />
              <Route path="/warehouse" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "operator", "warehouse", "admin"]}><WarehousePage /></Protected>} />
              <Route path="/raw-materials" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "operator", "warehouse", "admin"]}><RawMaterials /></Protected>} />
              <Route path="/support" element={<Protected><Assistant /></Protected>} />
              <Route path="/sales" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "sales", "admin"]}><ComingSoon titleKey="sales" /></Protected>} />
              <Route path="/spare-parts" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "sales", "admin"]}><ComingSoon titleKey="spare_parts" /></Protected>} />
              <Route path="/configurator" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "sales", "admin"]}><Configurator /></Protected>} />
              <Route path="/interventions" element={<Protected><Interventi /></Protected>} />
              <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
              <Route path="/gantt" element={<Protected><Gantt /></Protected>} />
              <Route path="/admin" element={<Protected roles={["management", "admin"]}><Admin /></Protected>} />
              <Route path="/analisi" element={<Protected roles={["management", "admin"]}><Analysis /></Protected>} />
              <Route path="/pianificazione" element={<ProtectedPlanning><PlanningProduction /></ProtectedPlanning>} />
              <Route path="/academy" element={<Protected><Academy /></Protected>} />
              <Route path="/academy/:id" element={<Protected><ProcedureDetail /></Protected>} />
              <Route path="/quality" element={<ProtectedQuality><Quality /></ProtectedQuality>} />
              <Route path="/purchasing" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "warehouse", "admin"]}><Purchasing /></Protected>} />
              <Route path="/supply-checks" element={<Protected roles={["management", "amministrazione", "warehouse", "admin"]}><SupplyChecks /></Protected>} />
              <Route path="/richieste-interne" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "warehouse", "operator", "sales", "admin"]}><RichiesteInterne /></Protected>} />
              <Route path="/distributors" element={<Protected roles={["management", "reparto_tecnico", "amministrazione", "sales", "admin"]}><ComingSoon titleKey="distributors" /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
